import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usuarioService, departamentoService, empresaService, usuarioProfileService, DiasTrabalhadosResposta, AtividadeAgregada } from '@/services/api';
import * as XLSX from 'xlsx';
import { saveOrShareBlob } from '@/lib/nativeFile';
import { sheetName, buildRelatorioUsoWorkbook, LinhaRelatorioUso, computeMetricasUsuario, MetricasUsuario } from '@/lib/usuarioExport';
import { veNotificacoesDeTodos } from '@/lib/relatorioUsoColunas';
import { Usuario, UserRole, NotificacoesPreferencias, Departamento, Empresa } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { CargoGestorSelects } from '@/components/usuarios/CargoGestorSelects';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { TabelaDesempenho } from '@/components/usuarios/UsuariosDesempenho';
import { useResumoDias } from '@/components/usuarios/useResumoDias';
import { ModulosDesempenho } from '@/components/usuarios/ModulosDesempenho';
import type { SinalFiltro } from '@/lib/diasTrabalhados';
import { PERIODO_PRESETS, PeriodoPreset, periodoDoPreset, periodoAnterior } from '@/lib/periodoFiltro';
import { Plus, User, Trash2, Loader2, Mail, Bell, X, Search, ChevronLeft, ChevronRight, Eye, EyeOff, Download, Users, TrendingUp, Activity, List, LayoutDashboard, Table as TableIcon, CalendarRange } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';

const getIniciais = (nome: string): string => {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0][0] ?? '';
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] ?? '' : '';
  return (primeira + ultima).toUpperCase();
};

const roleLabels: Record<UserRole, string> = {
  superadmin: 'Super Admin',
  admin: 'Administrador',
  comum: 'Usuário',
};

const roleStyles: Record<UserRole, string> = {
  superadmin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  comum: 'bg-gray-100 text-gray-600',
};

const defaultNotificacoes: NotificacoesPreferencias = {
  novos_okrs: true,
  atualizacao_okr: true,
  cancelamento_okr: true,
  alteracao_key_result: true,
  emails_notificacao: [],
};

const PAGE_SIZE = 10;
const SENHA_PADRAO = 'Admin123#';
// Radix Select não aceita value vazio: "Nenhum" no departamento do Novo Usuário usa este marcador.
const SEM_DEPARTAMENTO_FORM = '__sem_departamento__';

// Conteúdo do tooltip dos KPIs do dashboard — lista os nomes reais dos usuários daquele grupo
// (em vez de só o total agregado), com scroll pra grupos grandes.
function ListaNomesTooltip({ titulo, nomes }: { titulo: string; nomes: string[] }) {
  return (
    <div className="max-w-xs">
      <p className="font-medium mb-1">{titulo}</p>
      {nomes.length === 0 ? (
        <p className="text-muted-foreground">Nenhum usuário.</p>
      ) : (
        <ul className="max-h-56 overflow-y-auto space-y-0.5 pr-1">
          {nomes.map((nome, i) => (
            <li key={i} className="truncate">{nome}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Usuarios() {
  const { user, empresaAtiva, empresaFiltroId: empresaFiltroGlobal } = useAuth();
  // Filtro de empresa (superadmin) agora vem só do seletor global do header — sem estado
  // próprio aqui, pra não ter dois seletores fazendo a mesma coisa em lugares diferentes.
  const filtroEmpresaId = empresaFiltroGlobal || '';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Vindo de um clique nos cards de "Minha Empresa" (ex: card "Admins") — ?tipo=admin filtra a lista.
  const [filtroTipo, setFiltroTipo] = useState<string>(searchParams.get('tipo') || '');
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [todasEmpresas, setTodasEmpresas] = useState<Empresa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [exportandoResumo, setExportandoResumo] = useState(false);
  type AbaUsuarios = 'lista' | 'tabela' | 'semana' | 'modulos';
  const [abaAtiva, setAbaAtiva] = useState<AbaUsuarios>('lista');
  const [dashboardMetricas, setDashboardMetricas] = useState<MetricasUsuario[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardCarregado, setDashboardCarregado] = useState(false);
  const [dashboardPeriodo, setDashboardPeriodo] = useState<PeriodoPreset>('tudo');
  const [dashboardDias, setDashboardDias] = useState<DiasTrabalhadosResposta | null>(null);
  const [dashboardSinal, setDashboardSinal] = useState<SinalFiltro>('todos');
  const [dashboardAtividade, setDashboardAtividade] = useState<AtividadeAgregada | null>(null);
  const [dashboardAtividadeAnterior, setDashboardAtividadeAnterior] = useState<AtividadeAgregada | null>(null);
  const [soSinalizados, setSoSinalizados] = useState(false);
  // Mesmas métricas no período anterior de mesmo tamanho (comparação ↑↓); null sem período.
  const [dashboardAnterior, setDashboardAnterior] = useState<{
    metricas: MetricasUsuario[]; dias: DiasTrabalhadosResposta | null;
  } | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroDepartamentoId, setFiltroDepartamentoId] = useState('');
  const [departamentosFiltro, setDepartamentosFiltro] = useState<Departamento[]>([]);
  const [page, setPage] = useState(1);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNotificacoesDialogOpen, setIsNotificacoesDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    tipo: 'comum' as UserRole,
    empresa_id: '' as string,
    visualiza_apenas_proprio_dep: false,
    cargo_id: null as string | null,
    gestor_usuario_id: null as string | null,
    departamento_id: null as string | null,
  });
  const [formDataInitial, setFormDataInitial] = useState({
    nome: '',
    email: '',
    senha: '',
    tipo: 'comum' as UserRole,
    empresa_id: '' as string,
    visualiza_apenas_proprio_dep: false,
    cargo_id: null as string | null,
    gestor_usuario_id: null as string | null,
    departamento_id: null as string | null,
  });

  // Notificações form state
  const [notificacoesData, setNotificacoesData] = useState<NotificacoesPreferencias>(defaultNotificacoes);
  const [notificacoesDataInitial, setNotificacoesDataInitial] = useState<NotificacoesPreferencias>(defaultNotificacoes);

  // Unsaved changes state
  const [showUnsavedUser, setShowUnsavedUser] = useState(false);
  const [showUnsavedNotificacoes, setShowUnsavedNotificacoes] = useState(false);

  const hasUserChanges = useCallback(() => {
    return JSON.stringify(formData) !== JSON.stringify(formDataInitial);
  }, [formData, formDataInitial]);

  const hasNotificacoesChanges = useCallback(() => {
    return JSON.stringify(notificacoesData) !== JSON.stringify(notificacoesDataInitial);
  }, [notificacoesData, notificacoesDataInitial]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1);
      setSearchTerm(searchInput.trim());
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const loadSupportData = useCallback(async () => {
    try {
      if (user?.tipo === 'superadmin') {
        const empresasResult = await empresaService.getAll();
        if (empresasResult.success) setTodasEmpresas(empresasResult.data);
      } else {
        // Admin só pode ver/vincular empresas às quais ele próprio tem acesso
        const minhasEmpresas = (user?.empresas ?? []).map((e) => ({
          id: e.id,
          nome: e.nome,
          status: 'ativa' as const,
          data_criacao: '',
          modulos_habilitados: [],
        }));
        setTodasEmpresas(minhasEmpresas);
      }

      if (user?.tipo === 'admin' && empresaAtiva?.id) {
        const departamentosResult = await departamentoService.getByEmpresa(empresaAtiva.id);
        if (departamentosResult.success) setDepartamentos(departamentosResult.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    }
  }, [user, empresaAtiva]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const empresaId = user?.tipo === 'admin' ? empresaAtiva?.id : (filtroEmpresaId || undefined);
      // Departamento só é enviado quando há uma empresa definida (departamentos são por-empresa).
      const departamentoId = empresaId ? (filtroDepartamentoId || undefined) : undefined;

      if (user?.tipo === 'superadmin' || empresaId) {
        const usuariosResult = await usuarioService.getPaginated({
          page,
          limit: PAGE_SIZE,
          search: searchTerm,
          empresa_id: empresaId,
          departamento_id: departamentoId,
          tipo: filtroTipo || undefined,
        });

        if (usuariosResult.success) {
          setUsuarios(usuariosResult.data);
          setTotalUsuarios(usuariosResult.pagination.total);
          setTotalPages(usuariosResult.pagination.totalPages);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  }, [user, empresaAtiva, page, searchTerm, filtroEmpresaId, filtroDepartamentoId, filtroTipo]);

  // Invalida o dashboard já carregado quando os filtros mudam, pra recarregar na próxima vez
  // que a aba "Dashboard" for aberta (só refaz o fetch quando necessário, não a cada filtro).
  useEffect(() => {
    setDashboardCarregado(false);
  }, [user, empresaAtiva, searchTerm, filtroEmpresaId, filtroDepartamentoId, dashboardPeriodo]);

  useEffect(() => {
    if (user?.tipo === 'admin' || user?.tipo === 'superadmin') {
      loadSupportData();
    }
  }, [user, empresaAtiva, loadSupportData]);

  // Departamentos do filtro (superadmin): dependem da empresa escolhida no próprio filtro.
  useEffect(() => {
    if (user?.tipo !== 'superadmin') return;
    if (!filtroEmpresaId) {
      setDepartamentosFiltro([]);
      return;
    }
    let cancelado = false;
    departamentoService.getByEmpresa(filtroEmpresaId).then((res) => {
      if (!cancelado && res.success) setDepartamentosFiltro(res.data);
    });
    return () => {
      cancelado = true;
    };
  }, [user, filtroEmpresaId]);

  // Trocar a empresa do filtro invalida o departamento selecionado (é de outra empresa).
  useEffect(() => {
    setFiltroDepartamentoId('');
  }, [filtroEmpresaId]);

  useEffect(() => {
    setPage(1);
  }, [filtroEmpresaId, filtroDepartamentoId]);

  useEffect(() => {
    if (user?.tipo === 'admin' || user?.tipo === 'superadmin') {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [user, empresaAtiva, loadData]);

  // Empresa do "Novo Usuário": a escolhida pelo superadmin ou, sem escolha, a ativa.
  const empresaDoFormulario = formData.empresa_id || empresaAtiva?.id || '';
  // Departamentos dessa empresa: o admin reaproveita a lista que a página já carregou (mesma empresa ativa);
  // só a empresa escolhida pelo superadmin precisa de busca. `null` = ainda carregando.
  const usaDepartamentosDaPagina = user?.tipo === 'admin' && empresaDoFormulario === empresaAtiva?.id;
  const [departamentosBuscados, setDepartamentosBuscados] = useState<{ empresaId: string; lista: Departamento[] } | null>(null);
  useEffect(() => {
    if (!isDialogOpen || !empresaDoFormulario || usaDepartamentosDaPagina) return;
    if (departamentosBuscados?.empresaId === empresaDoFormulario) return;
    let cancelado = false;
    departamentoService.getByEmpresa(empresaDoFormulario).then((res) => {
      if (!cancelado) setDepartamentosBuscados({ empresaId: empresaDoFormulario, lista: res.success ? res.data : [] });
    });
    return () => {
      cancelado = true;
    };
  }, [isDialogOpen, empresaDoFormulario, usaDepartamentosDaPagina, departamentosBuscados]);
  const departamentosFormulario: Departamento[] | null = usaDepartamentosDaPagina
    ? departamentos
    : departamentosBuscados?.empresaId === empresaDoFormulario
      ? departamentosBuscados.lista
      : null;

  const handleSave = async () => {
    const empresaId = empresaDoFormulario;

    if (user?.tipo === 'superadmin' && !formData.empresa_id && formData.tipo === 'comum') {
      toast.error('Selecione uma empresa para o usuário');
      return;
    }
    if (formData.tipo === 'comum' && !empresaId) return;

    try {
      const result = await usuarioService.create({
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
        tipo: formData.tipo,
        empresa_id: empresaId,
        visualiza_apenas_proprio_dep: formData.tipo === 'comum' ? formData.visualiza_apenas_proprio_dep : false,
        cargo_id: formData.cargo_id,
        gestor_usuario_id: formData.gestor_usuario_id,
        departamento_id: formData.departamento_id,
      });
      if (result.success) {
        toast.success('Usuário criado');
        loadData();
        resetForm();
      } else {
        toast.error(result.error || 'Erro ao criar usuário');
      }
    } catch (error) {
      toast.error('Erro ao salvar usuário');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;

    try {
      const result = await usuarioService.delete(deleteConfirmId);
      if (result.success) {
        toast.success(
          result.escopo === 'acesso_da_empresa'
            ? 'Acesso à empresa removido (a pessoa continua usando o Kora em outra empresa)'
            : 'Usuário excluído',
        );
        loadData();
      } else {
        toast.error(result.error || 'Erro ao excluir usuário');
      }
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
    setDeleteConfirmId(null);
  };

  const resetForm = () => {
    const initial = {
      nome: '',
      email: '',
      senha: '',
      tipo: 'comum' as UserRole,
      empresa_id: user?.tipo === 'superadmin' ? '' : (empresaAtiva?.id || ''),
      visualiza_apenas_proprio_dep: false,
      cargo_id: null as string | null,
      gestor_usuario_id: null as string | null,
      departamento_id: null as string | null,
    };
    setFormData(initial);
    setFormDataInitial(initial);
    setEditingUser(null);
    setIsDialogOpen(false);
  };

  const openNewUser = () => {
    const initial = {
      nome: '',
      email: '',
      senha: SENHA_PADRAO,
      tipo: 'comum' as UserRole,
      empresa_id: user?.tipo === 'superadmin' ? '' : (empresaAtiva?.id || ''),
      visualiza_apenas_proprio_dep: false,
      cargo_id: null as string | null,
      gestor_usuario_id: null as string | null,
      departamento_id: null as string | null,
    };
    setFormData(initial);
    setFormDataInitial(initial);
    setEditingUser(null);
    setShowPassword(false);
    setIsDialogOpen(true);
  };

  const handleCloseUserDialog = useCallback(() => {
    if (hasUserChanges()) {
      setShowUnsavedUser(true);
    } else {
      resetForm();
    }
  }, [hasUserChanges]);

  const getDepartamentoNome = (departamentoId?: string | null) => {
    if (!departamentoId) return '-';
    const dep = departamentos.find(d => d.id === departamentoId);
    return dep?.nome || '-';
  };

  const getEmpresaNome = (empresaId?: string | null) => {
    if (!empresaId) return '-';
    const emp = todasEmpresas.find(e => e.id === empresaId);
    return emp?.nome || '-';
  };

  const buscarUsuariosFiltrados = async (): Promise<Usuario[] | null> => {
    const empresaId = user?.tipo === 'admin' ? empresaAtiva?.id : (filtroEmpresaId || undefined);
    const departamentoId = empresaId ? (filtroDepartamentoId || undefined) : undefined;

    const todosResult = await usuarioService.getPaginated({
      page: 1,
      limit: 9999,
      search: searchTerm,
      empresa_id: empresaId,
      departamento_id: departamentoId,
      tipo: filtroTipo || undefined,
    });
    if (!todosResult.success || todosResult.data.length === 0) {
      toast.error('Nenhum usuário para exportar com os filtros atuais');
      return null;
    }
    return todosResult.data;
  };

  // Formato "Base Detalhada" aprovado com a usuária em 26/08/2026 (ver
  // docs/proposta-relatorio-uso-2026-08-26.md) — substitui o antigo formato de várias abas.
  const handleBaixarResumo = async () => {
    setExportandoResumo(true);
    try {
      const alvo = await buscarUsuariosFiltrados();
      if (!alvo) return;

      const resumosPorUsuario = await Promise.all(
        alvo.map((u) => usuarioProfileService.getResumo(u.id))
      );

      const empresasDoUsuario = (u: Usuario) => {
        const nomes = getUsuarioEmpresas(u).map((e) => e.nome);
        return nomes.length > 0 ? nomes.join(', ') : getEmpresaNome(u.empresa_id);
      };

      const linhas: LinhaRelatorioUso[] = alvo.map((u, idx) => ({
        usuario: u,
        resumo: resumosPorUsuario[idx],
        empresas: empresasDoUsuario(u),
        departamento: getDepartamentoNome(u.departamento_id),
      }));

      const workbook = await buildRelatorioUsoWorkbook(linhas, { incluirNotificacoes: veNotificacoesDeTodos(user?.tipo) });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      await saveOrShareBlob(blob, `relatorio_uso_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Exportação concluída');
    } catch (error) {
      console.error('Error exporting all users:', error);
      toast.error('Erro ao exportar dados dos usuários');
    } finally {
      setExportandoResumo(false);
    }
  };


  const getUsuarioEmpresas = (usuario: Usuario) => {
    const empresas = (usuario.usuarioEmpresas ?? [])
      .filter((ue) => ue.ativo)
      .map((ue) => ({
        id: ue.empresa_id,
        nome: ue.empresa?.nome || getEmpresaNome(ue.empresa_id),
      }))
      .filter((empresa) => empresa.nome !== '-');

    if (empresas.length === 0 && usuario.empresa_id) {
      empresas.push({ id: usuario.empresa_id, nome: getEmpresaNome(usuario.empresa_id) });
    }

    return Array.from(new Map(empresas.map((empresa) => [empresa.id, empresa])).values());
  };

  // Dashboard de desempenho — reaproveita os mesmos providers de resumo e o mesmo cálculo de
  // métricas (Produtividade, Nível de Utilização, Status de Acesso) já usados no relatório de uso
  // "Baixar resumo" (ver docs/proposta-relatorio-uso-2026-08-26.md).
  const loadDashboardMetricas = async () => {
    setDashboardLoading(true);
    try {
      const alvo = await buscarUsuariosFiltrados();
      if (!alvo) {
        setDashboardMetricas([]);
        setDashboardDias(null);
        setDashboardAnterior(null);
        return;
      }
      const periodo = periodoDoPreset(dashboardPeriodo);
      const anterior = periodoAnterior(periodo);
      const ids = alvo.map((u) => u.id);
      setDashboardAtividade(null);
      setDashboardAtividadeAnterior(null);
      // Ações agregadas (aba Módulos e medida "Ações registradas") — em paralelo, sem segurar o resto.
      usuarioProfileService.getAtividadeAgregada(ids, periodo).then(setDashboardAtividade);
      if (anterior) usuarioProfileService.getAtividadeAgregada(ids, anterior).then(setDashboardAtividadeAnterior);
      const [resumos, dias, resumosAnt, diasAnt] = await Promise.all([
        Promise.all(alvo.map((u) => usuarioProfileService.getResumo(u.id, undefined, periodo))),
        usuarioProfileService.getDiasTrabalhadosLista(ids, periodo),
        anterior ? Promise.all(alvo.map((u) => usuarioProfileService.getResumo(u.id, undefined, anterior))) : null,
        anterior ? usuarioProfileService.getDiasTrabalhadosLista(ids, anterior) : null,
      ]);
      setDashboardDias(dias);
      const montar = (lista: typeof resumos) => alvo.map((usuario, idx) => {
        const nomes = getUsuarioEmpresas(usuario).map((e) => e.nome);
        return computeMetricasUsuario({
          usuario,
          resumo: lista[idx],
          empresas: nomes.length > 0 ? nomes.join(', ') : getEmpresaNome(usuario.empresa_id),
          departamento: getDepartamentoNome(usuario.departamento_id),
        });
      });
      const metricas = montar(resumos);
      setDashboardAnterior(resumosAnt ? { metricas: montar(resumosAnt), dias: diasAnt } : null);
      setDashboardMetricas(metricas);
      setDashboardCarregado(true);
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
      toast.error('Erro ao carregar dashboard de desempenho');
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleAbaChange = (v: string) => {
    setAbaAtiva(v as AbaUsuarios);
  };

  // Recarrega o dashboard sempre que ele ficar "não carregado" (filtro de empresa mudou —
  // ver invalidação acima) enquanto a aba já está aberta, não só ao clicar nela.
  useEffect(() => {
    if (abaAtiva !== 'lista' && !dashboardCarregado && !dashboardLoading) {
      loadDashboardMetricas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva, dashboardCarregado, dashboardLoading]);

  const openNotificacoes = (usuario: Usuario) => {
    setEditingUser(usuario);
    const notifData = usuario.notificacoes || defaultNotificacoes;
    setNotificacoesData(notifData);
    setNotificacoesDataInitial(notifData);
    setIsNotificacoesDialogOpen(true);
  };

  const handleCloseNotificacoesDialog = useCallback(() => {
    if (hasNotificacoesChanges()) {
      setShowUnsavedNotificacoes(true);
    } else {
      setIsNotificacoesDialogOpen(false);
      setEditingUser(null);
    }
  }, [hasNotificacoesChanges]);

  const handleSaveNotificacoes = async () => {
    if (!editingUser) return;

    try {
      const emailsNotificacao = notificacoesData.emails_notificacao || [];
      const emailsFiltrados = emailsNotificacao
        .filter((email: string) => email && email.trim().length > 0)
        .slice(0, 2);

      const result = await usuarioService.update(editingUser.id, {
        notificacoes: {
          ...notificacoesData,
          emails_notificacao: emailsFiltrados,
        },
      });
      
      if (result.success) {
        toast.success('Preferências de notificação atualizadas');
        loadData();
        setIsNotificacoesDialogOpen(false);
        setEditingUser(null);
      } else {
        toast.error(result.error || 'Erro ao salvar preferências');
      }
    } catch (error) {
      toast.error('Erro ao salvar preferências');
    }
  };

  // Dados derivados pro dashboard de desempenho (recalculados só quando as métricas mudam).
  // Precisam ficar antes do "return" condicional abaixo — hooks não podem ser chamados
  // condicionalmente (regra do React), senão a ordem dos hooks muda entre renders.
  const dashboardResumo = useMemo(() => {
    const total = dashboardMetricas.length;
    const ativos = dashboardMetricas.filter((m) => m.statusAcessoLabel === 'Ativo').length;
    const acompanhamento = dashboardMetricas.filter((m) => m.statusAcessoLabel === 'Em acompanhamento').length;
    const nuncaAcessou = dashboardMetricas.filter((m) => m.statusAcessoLabel === 'Nunca acessou').length;
    const inativosSemContarNunca = dashboardMetricas.filter((m) => m.statusAcessoLabel === 'Inativo').length;
    const inativos = inativosSemContarNunca + nuncaAcessou;
    const produtividadeMedia = total > 0
      ? Math.round((dashboardMetricas.reduce((s, m) => s + m.produtividade, 0) / total) * 100) / 100
      : 0;
    const produtividades = dashboardMetricas.map((m) => m.produtividade);
    const produtividadeMin = produtividades.length > 0 ? Math.min(...produtividades) : 0;
    const produtividadeMax = produtividades.length > 0 ? Math.max(...produtividades) : 0;
    const nivelAlto = dashboardMetricas.filter((m) => m.nivelUtilizacao === 'Alto').length;
    const nivelMedio = dashboardMetricas.filter((m) => m.nivelUtilizacao === 'Médio').length;
    const nivelBaixo = dashboardMetricas.filter((m) => m.nivelUtilizacao === 'Baixo').length;
    const nomesTotal = dashboardMetricas.map((m) => m.nome);
    const nomesAtivos = dashboardMetricas.filter((m) => m.statusAcessoLabel === 'Ativo').map((m) => m.nome);
    const nomesAcompanhamento = dashboardMetricas.filter((m) => m.statusAcessoLabel === 'Em acompanhamento').map((m) => m.nome);
    const nomesInativos = dashboardMetricas
      .filter((m) => m.statusAcessoLabel === 'Inativo' || m.statusAcessoLabel === 'Nunca acessou')
      .map((m) => m.nome);
    const produtividadePorNome = [...dashboardMetricas]
      .sort((a, b) => b.produtividade - a.produtividade)
      .map((m) => `${m.nome} (${m.produtividade.toFixed(2)})`);
    return {
      total, ativos, acompanhamento, inativos, produtividadeMedia,
      nuncaAcessou, inativosSemContarNunca, produtividadeMin, produtividadeMax,
      nivelAlto, nivelMedio, nivelBaixo,
      nomesTotal, nomesAtivos, nomesAcompanhamento, nomesInativos, produtividadePorNome,
    };
  }, [dashboardMetricas]);

  const periodoDashboard = useMemo(() => periodoDoPreset(dashboardPeriodo), [dashboardPeriodo]);
  const periodoDashboardAnterior = useMemo(() => periodoAnterior(periodoDashboard) ?? {}, [periodoDashboard]);
  const diasResumo = useResumoDias(dashboardMetricas, dashboardDias, periodoDashboard, dashboardSinal);
  const diasResumoAnterior = useResumoDias(
    dashboardAnterior?.metricas ?? [], dashboardAnterior?.dias ?? null, periodoDashboardAnterior, dashboardSinal,
  );
  const metricasAnteriores = useMemo(
    () => (dashboardAnterior ? new Map(dashboardAnterior.metricas.map((m) => [m.id, m])) : null),
    [dashboardAnterior],
  );

  if (user?.tipo !== 'admin' && user?.tipo !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>

        <GuiaModuloFlutuante imagemSrc="/guias-modulos/usuarios.png" titulo="Usuários" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="rounded-lg border bg-card p-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Usuários</h1>
            <p className="text-muted-foreground">
              Gerencie os usuários da sua empresa
            </p>
          </div>
          <Button onClick={openNewUser} data-tour="novo-usuario">
            <Plus className="h-4 w-4 mr-2" />
            Novo Usuário
          </Button>
        </div>

        <Tabs value={abaAtiva} onValueChange={handleAbaChange} className="space-y-4">
          {/* Abas e período na mesma linha — o período vale pras 3 abas do dashboard */}
          <div className="flex flex-wrap items-center justify-between gap-3">
          {(user?.tipo === 'admin' || user?.tipo === 'superadmin') && (
            <TabsList>
              <TabsTrigger value="lista" className="flex items-center gap-2">
                <List className="h-4 w-4" />
                Diretório
              </TabsTrigger>
              <TabsTrigger value="tabela" className="flex items-center gap-2">
                <TableIcon className="h-4 w-4" />
                Comparativo
              </TabsTrigger>
              <TabsTrigger value="semana" className="flex items-center gap-2">
                <CalendarRange className="h-4 w-4" />
                Dias trabalhados
              </TabsTrigger>
              <TabsTrigger value="modulos" className="flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4" />
                Módulos
              </TabsTrigger>
            </TabsList>
          )}
          {(abaAtiva === 'semana' || abaAtiva === 'modulos') && (
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Período</Label>
              <Select value={dashboardPeriodo} onValueChange={(v) => setDashboardPeriodo(v as PeriodoPreset)}>
                <SelectTrigger className="h-9 w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIODO_PRESETS.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          </div>

          <TabsContent value="lista" className="space-y-4">
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center">
                <div className="relative w-full md:max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Pesquisar por nome ou email..."
                    className="h-9 pl-9 pr-10"
                  />
                  {searchInput && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                      onClick={() => setSearchInput('')}
                      title="Limpar pesquisa"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {filtroTipo && (
                  <Badge variant="secondary" className="flex items-center gap-1.5 font-normal">
                    Filtrando: {roleLabels[filtroTipo as UserRole] || filtroTipo}
                    <button
                      type="button"
                      onClick={() => {
                        setFiltroTipo('');
                        const next = new URLSearchParams(searchParams);
                        next.delete('tipo');
                        setSearchParams(next, { replace: true });
                      }}
                      className="ml-0.5"
                      aria-label="Limpar filtro de tipo"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}

                {(user?.tipo === 'admin' || (user?.tipo === 'superadmin' && filtroEmpresaId)) && (
                  <Select
                    value={filtroDepartamentoId || 'todos'}
                    onValueChange={(v) => setFiltroDepartamentoId(v === 'todos' ? '' : v)}
                  >
                    <SelectTrigger className="h-9 w-full text-sm md:w-52">
                      <SelectValue placeholder="Todos os departamentos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os departamentos</SelectItem>
                      {(user?.tipo === 'superadmin' ? departamentosFiltro : departamentos).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {(user?.tipo === 'admin' || user?.tipo === 'superadmin') && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs"
                    disabled={exportandoResumo}
                    onClick={handleBaixarResumo}
                    title="Uma planilha só, com o resumo de todos os usuários filtrados"
                  >
                    {exportandoResumo ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    <span className="ml-1.5">Baixar resumo</span>
                  </Button>
                )}
              </div>
              <span className="text-sm text-muted-foreground">
                {usuarios.length} de {totalUsuarios} usuário(s)
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Departamento</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Tipo</TableHead>
                  {user?.tipo === 'superadmin' && <TableHead>Último acesso</TableHead>}
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.map((usuario) => {
                  const empresasUsuario = getUsuarioEmpresas(usuario);

                  return (
                  <TableRow
                    key={usuario.id}
                    className={cn((user?.tipo === 'superadmin' || user?.tipo === 'admin') && 'cursor-pointer')}
                    onClick={() => {
                      if (user?.tipo === 'superadmin' || user?.tipo === 'admin') navigate(`/usuarios/${usuario.id}`);
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={usuario.avatar_url ?? undefined} alt={usuario.nome} />
                          <AvatarFallback>{getIniciais(usuario.nome)}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{usuario.nome}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <span>{usuario.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user?.tipo === 'superadmin' ? (
                        <div className="flex max-w-72 flex-wrap gap-1.5">
                          {empresasUsuario.map((empresa) => (
                            <Badge key={empresa.id} variant="outline" className="font-normal">
                              {empresa.nome}
                            </Badge>
                          ))}
                          {empresasUsuario.length === 0 && (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">{getEmpresaNome(usuario.empresa_id)}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{getDepartamentoNome(usuario.departamento_id)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{usuario.cargo_nome || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("font-normal", roleStyles[usuario.tipo])}>
                        {roleLabels[usuario.tipo]}
                      </Badge>
                    </TableCell>
                    {user?.tipo === 'superadmin' && (
                      <TableCell className="text-sm text-muted-foreground">
                        {usuario.ultimo_acesso ? new Date(usuario.ultimo_acesso).toLocaleString('pt-BR') : 'Nunca acessou'}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); openNotificacoes(usuario); }}
                          title="Preferências de notificação"
                        >
                          <Bell className="h-4 w-4" />
                        </Button>
                        {usuario.id !== user?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(usuario.id); }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}

                {usuarios.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={user?.tipo === 'superadmin' ? 8 : 7} className="text-center py-8 text-muted-foreground">
                      {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    title="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    title="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* min-w-0 em todas as abas do dashboard: sem isso a tabela larga estica a página inteira */}
          <TabsContent value="tabela" className="min-w-0">
              {dashboardLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : dashboardMetricas.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Users className="h-12 w-12 mb-3 opacity-50" />
                  <p className="text-sm">Nenhum usuário para mostrar com os filtros atuais</p>
                </div>
              ) : (
                <TabelaDesempenho
                  visao="usuario"
                  metricas={dashboardMetricas}
                  mostrarColunaEmpresa={user?.tipo === 'superadmin' && !filtroEmpresaId}
                  periodoPreset={dashboardPeriodo}
                  periodo={periodoDashboard}
                  dias={diasResumo}
                  diasTrabalhados={dashboardDias}
                  sinal={dashboardSinal}
                  onSinalChange={setDashboardSinal}
                  soSinalizados={soSinalizados}
                  onSoSinalizadosChange={setSoSinalizados}
                  anteriores={metricasAnteriores}
                  diasAnteriores={dashboardAnterior ? diasResumoAnterior : null}
                  onPeriodoChange={setDashboardPeriodo}
                />
              )}
          </TabsContent>

          <TabsContent value="semana" className="min-w-0">
              {dashboardLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : dashboardMetricas.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Users className="h-12 w-12 mb-3 opacity-50" />
                  <p className="text-sm">Nenhum usuário para mostrar com os filtros atuais</p>
                </div>
              ) : (
                <TabelaDesempenho
                  visao="semana"
                  metricas={dashboardMetricas}
                  mostrarColunaEmpresa={user?.tipo === 'superadmin' && !filtroEmpresaId}
                  periodoPreset={dashboardPeriodo}
                  periodo={periodoDashboard}
                  dias={diasResumo}
                  diasTrabalhados={dashboardDias}
                  sinal={dashboardSinal}
                  onSinalChange={setDashboardSinal}
                  soSinalizados={soSinalizados}
                  onSoSinalizadosChange={setSoSinalizados}
                  anteriores={metricasAnteriores}
                  diasAnteriores={dashboardAnterior ? diasResumoAnterior : null}
                />
              )}
          </TabsContent>

          <TabsContent value="modulos" className="min-w-0">
              {dashboardLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : dashboardMetricas.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Users className="h-12 w-12 mb-3 opacity-50" />
                  <p className="text-sm">Nenhum usuário para mostrar com os filtros atuais</p>
                </div>
              ) : (
                <ModulosDesempenho
                  metricas={dashboardMetricas}
                  periodoPreset={dashboardPeriodo}
                  atividade={dashboardAtividade}
                  mostrarEmpresas={user?.tipo === 'superadmin'}
                />
              )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) handleCloseUserDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Usuário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {user?.tipo === 'superadmin' && (
              <div className="space-y-2">
                <Label htmlFor="user_empresa">Empresa *</Label>
                <Select
                  value={formData.empresa_id}
                  onValueChange={(v) => setFormData({ ...formData, empresa_id: v, cargo_id: null, gestor_usuario_id: null, departamento_id: null })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {todasEmpresas.filter((e) => e.status === 'ativa').map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="user_nome">Nome</Label>
              <Input
                id="user_nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Nome completo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user_email">Email</Label>
              <Input
                id="user_email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@empresa.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user_senha">Senha</Label>
              <div className="relative">
                <Input
                  id="user_senha"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  placeholder="••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Senha padrão pré-preenchida. Edite o campo se quiser definir outra antes de criar.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user_tipo">Tipo de Usuário</Label>
              <Select
                value={formData.tipo}
                onValueChange={(v) => setFormData({ ...formData, tipo: v as UserRole })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="comum">Usuário Comum</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {empresaDoFormulario && (
              <div className="space-y-2">
                <Label htmlFor="user_departamento">Departamento</Label>
                <Select
                  value={departamentosFormulario ? (formData.departamento_id ?? SEM_DEPARTAMENTO_FORM) : ''}
                  onValueChange={(v) => setFormData({ ...formData, departamento_id: v === SEM_DEPARTAMENTO_FORM ? null : v })}
                  disabled={!departamentosFormulario}
                >
                  <SelectTrigger id="user_departamento">
                    <SelectValue placeholder="Carregando departamentos…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_DEPARTAMENTO_FORM}>Nenhum</SelectItem>
                    {(departamentosFormulario ?? []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Onde a pessoa aparece no organograma. Sem departamento, vale o do cargo.
                </p>
              </div>
            )}
            {empresaDoFormulario && (
              <CargoGestorSelects
                key={empresaDoFormulario}
                empresaId={empresaDoFormulario}
                cargoId={formData.cargo_id}
                gestorId={formData.gestor_usuario_id}
                gestorNome={editingUser?.gestor_nome}
                usuarioId={editingUser?.id}
                onChange={(campo, valor) => setFormData({ ...formData, [campo]: valor })}
              />
            )}
          </div>

          {formData.tipo === 'comum' && (
            <div className="flex items-start justify-between rounded-lg border p-3 gap-4">
              <div className="space-y-0.5">
                <Label>Só visualiza OKRs do próprio departamento</Label>
                <p className="text-xs text-muted-foreground">
                  Quando ativo, o usuário verá apenas OKRs do departamento vinculado
                </p>
              </div>
              <Switch
                checked={formData.visualiza_apenas_proprio_dep}
                onCheckedChange={(v) =>
                  setFormData({ ...formData, visualiza_apenas_proprio_dep: v })
                }
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseUserDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.nome || !formData.email || !formData.senha}
            >
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Notificações Dialog */}
      <Dialog open={isNotificacoesDialogOpen} onOpenChange={(open) => { if (!open) handleCloseNotificacoesDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Preferências de Notificação
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Configurar notificações por email para: <strong>{editingUser?.nome}</strong>
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="notif_novos" className="flex-1">
                  Receber email de novos OKRs
                </Label>
                <Switch
                  id="notif_novos"
                  checked={notificacoesData.novos_okrs}
                  onCheckedChange={(checked) => 
                    setNotificacoesData({ ...notificacoesData, novos_okrs: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="notif_atualizacao" className="flex-1">
                  Receber email de atualização de OKR
                </Label>
                <Switch
                  id="notif_atualizacao"
                  checked={notificacoesData.atualizacao_okr}
                  onCheckedChange={(checked) => 
                    setNotificacoesData({ ...notificacoesData, atualizacao_okr: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="notif_cancelamento" className="flex-1">
                  Receber email de cancelamento de OKR
                </Label>
                <Switch
                  id="notif_cancelamento"
                  checked={notificacoesData.cancelamento_okr}
                  onCheckedChange={(checked) => 
                    setNotificacoesData({ ...notificacoesData, cancelamento_okr: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="notif_kr" className="flex-1">
                  Receber email de alteração de Key Result
                </Label>
                <Switch
                  id="notif_kr"
                  checked={notificacoesData.alteracao_key_result}
                  onCheckedChange={(checked) => 
                    setNotificacoesData({ ...notificacoesData, alteracao_key_result: checked })
                  }
                />
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t">
              <Label className="text-sm font-medium">Emails para notificação (máx. 2)</Label>
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Email principal"
                  value={notificacoesData.emails_notificacao?.[0] || ''}
                  onChange={(e) => {
                    const emails = [...(notificacoesData.emails_notificacao || [])];
                    emails[0] = e.target.value;
                    // Manter array com 2 posições para garantir que o backend receba corretamente
                    while (emails.length < 2) emails.push('');
                    setNotificacoesData({ ...notificacoesData, emails_notificacao: emails });
                  }}
                />
                <Input
                  type="email"
                  placeholder="Email secundário (opcional)"
                  value={notificacoesData.emails_notificacao?.[1] || ''}
                  onChange={(e) => {
                    const emails = [...(notificacoesData.emails_notificacao || [])];
                    // Garantir que temos pelo menos 2 posições
                    while (emails.length < 2) emails.push('');
                    emails[1] = e.target.value;
                    setNotificacoesData({ ...notificacoesData, emails_notificacao: emails });
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Se nenhum email for informado, as notificações serão enviadas para o email do usuário.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseNotificacoesDialog}>
              Cancelar
            </Button>
            <Button onClick={handleSaveNotificacoes}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved User Confirmation */}
      <UnsavedChangesDialog
        open={showUnsavedUser}
        onOpenChange={setShowUnsavedUser}
        onConfirm={() => {
          setShowUnsavedUser(false);
          resetForm();
        }}
        onCancel={() => setShowUnsavedUser(false)}
      />

      {/* Unsaved Notificacoes Confirmation */}
      <UnsavedChangesDialog
        open={showUnsavedNotificacoes}
        onOpenChange={setShowUnsavedNotificacoes}
        onConfirm={() => {
          setShowUnsavedNotificacoes(false);
          setIsNotificacoesDialogOpen(false);
          setEditingUser(null);
        }}
        onCancel={() => setShowUnsavedNotificacoes(false)}
      />

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/usuarios.png" titulo="Usuários" />
    </AppLayout>
  );
}
